import { supabase } from "./supabase";
import apolloService from "./apolloService";

/**
 * Enhanced Campaign Service with Apollo Integration
 * Handles campaign creation, lead fetching, and comprehensive statistics
 */

const campaignServiceEnhanced = {
  /**
   * Get all campaigns for a user with statistics
   */
  getCampaigns: async (userId) => {
    try {
      const { data, error } = await supabase
        .from("campaigns_enhanced")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error("Error in getCampaigns:", error);
      return { success: false, error: "Failed to load campaigns" };
    }
  },

  /**
   * Get campaign with full statistics
   */
  getCampaignWithStats: async (campaignId) => {
    try {
      const { data: campaign, error: campaignError } = await supabase
        .from("campaigns_enhanced")
        .select("*")
        .eq("id", campaignId)
        .single();

      if (campaignError) {
        return { success: false, error: campaignError.message };
      }

      // Get lead statistics using the view
      const { data: stats, error: statsError } = await supabase
        .from("vw_campaign_engagement_metrics")
        .select("*")
        .eq("campaign_id", campaignId)
        .single();

      if (statsError) {
        console.warn("Could not fetch campaign stats:", statsError);
      }

      return {
        success: true,
        data: {
          ...campaign,
          statistics: stats || null,
        },
      };
    } catch (error) {
      console.error("Error in getCampaignWithStats:", error);
      return { success: false, error: "Failed to load campaign" };
    }
  },

  /**
   * Create a new campaign
   */
  createCampaign: async (campaignData) => {
    try {
      const payload = {
        ...campaignData,
        campaign_status: campaignData.campaign_status || "draft",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from("campaigns_enhanced")
        .insert(payload)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      // Log campaign creation activity
      await campaignServiceEnhanced.logActivity(
        data.id,
        campaignData.user_id,
        "created",
        `Campaign "${campaignData.name}" created successfully`
      );

      return { success: true, data };
    } catch (error) {
      console.error("Error in createCampaign:", error);
      return { success: false, error: "Failed to create campaign" };
    }
  },

  /**
   * Update campaign
   */
  updateCampaign: async (campaignId, updates) => {
    try {
      const { data, error } = await supabase
        .from("campaigns_enhanced")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", campaignId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error("Error in updateCampaign:", error);
      return { success: false, error: "Failed to update campaign" };
    }
  },

  /**
   * Update campaign status
   */
  updateCampaignStatus: async (campaignId, status, userId) => {
    try {
      const validStatuses = [
        "draft",
        "not_started",
        "processing",
        "active",
        "paused",
        "completed",
        "failed",
      ];
      if (!validStatuses.includes(status)) {
        return { success: false, error: `Invalid campaign status: ${status}` };
      }

      const { data, error } = await supabase
        .from("campaigns_enhanced")
        .update({
          campaign_status: status,
          updated_at: new Date().toISOString(),
          ...(status === "completed" && {
            completed_at: new Date().toISOString(),
          }),
          ...(status === "active" && { started_at: new Date().toISOString() }),
        })
        .eq("id", campaignId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      // Log status change
      await campaignServiceEnhanced.logActivity(
        campaignId,
        userId,
        "updated",
        `Campaign status changed to ${status}`
      );

      return { success: true, data };
    } catch (error) {
      console.error("Error in updateCampaignStatus:", error);
      return { success: false, error: "Failed to update campaign status" };
    }
  },

  /**
   * Fetch leads from Apollo for a campaign
   */
  fetchLeadsFromApollo: async (campaignId, userId, oauthToken = null) => {
    try {
      // Get campaign details
      const { data: campaign, error: campaignError } = await supabase
        .from("campaigns_enhanced")
        .select("*")
        .eq("id", campaignId)
        .single();

      if (campaignError) {
        return { success: false, error: "Campaign not found" };
      }

      // Update campaign status to processing
      await campaignServiceEnhanced.updateCampaignStatus(
        campaignId,
        "processing",
        userId
      );

      // Search for leads using Apollo
      const searchResult = await apolloService.searchLeads(
        {
          jobTitles: campaign.target_job_titles || [],
          industries: campaign.target_industries || [],
          locations: campaign.target_locations || [],
          companySizes: campaign.target_company_sizes || [],
          limit: campaign.max_leads_to_fetch || 100,
        },
        oauthToken
      );

      if (!searchResult.success) {
        await campaignServiceEnhanced.updateCampaignStatus(
          campaignId,
          "failed",
          userId
        );
        return { success: false, error: searchResult.error };
      }

      // Prepare leads for insertion
      const leads = searchResult.data.leads.map((lead) => ({
        ...lead,
        campaign_id: campaignId,
        user_id: userId,
        fetched_at: new Date().toISOString(),
      }));

      // Insert leads into database
      const { data: insertedLeads, error: insertError } = await supabase
        .from("apollo_leads")
        .insert(leads)
        .select();

      if (insertError) {
        console.error("Error inserting leads:", insertError);
        await campaignServiceEnhanced.updateCampaignStatus(
          campaignId,
          "failed",
          userId
        );
        return { success: false, error: "Failed to save leads to database" };
      }

      // Update campaign with lead count and Apollo sync status
      await campaignServiceEnhanced.updateCampaign(campaignId, {
        apollo_leads_count: insertedLeads.length,
        apollo_sync_status: "synced",
        total_leads: insertedLeads.length,
      });

      // Update campaign status to completed
      await campaignServiceEnhanced.updateCampaignStatus(
        campaignId,
        "completed",
        userId
      );

      // Log activity
      await campaignServiceEnhanced.logActivity(
        campaignId,
        userId,
        "leads_fetched",
        `Successfully fetched ${insertedLeads.length} leads from Apollo`
      );

      return {
        success: true,
        data: {
          leadsCount: insertedLeads.length,
          leads: insertedLeads,
          totalAvailable: searchResult.data.total,
        },
      };
    } catch (error) {
      console.error("Error in fetchLeadsFromApollo:", error);
      return { success: false, error: "Failed to fetch leads from Apollo" };
    }
  },

  /**
   * Get leads for a campaign
   */
  getLeadsForCampaign: async (campaignId, limit = 100, offset = 0) => {
    try {
      const { data, error, count } = await supabase
        .from("apollo_leads")
        .select("*", { count: "exact" })
        .eq("campaign_id", campaignId)
        .order("created_at", { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: {
          leads: data,
          total: count,
          limit,
          offset,
        },
      };
    } catch (error) {
      console.error("Error in getLeadsForCampaign:", error);
      return { success: false, error: "Failed to load leads" };
    }
  },

  /**
   * Get lead details
   */
  getLeadDetails: async (leadId) => {
    try {
      const { data, error } = await supabase
        .from("apollo_leads")
        .select("*")
        .eq("id", leadId)
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error("Error in getLeadDetails:", error);
      return { success: false, error: "Failed to load lead details" };
    }
  },

  /**
   * Update lead information
   */
  updateLead: async (leadId, updates) => {
    try {
      const { data, error } = await supabase
        .from("apollo_leads")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", leadId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error("Error in updateLead:", error);
      return { success: false, error: "Failed to update lead" };
    }
  },

  /**
   * Update lead status
   */
  updateLeadStatus: async (leadId, status) => {
    try {
      const validStatuses = [
        "new",
        "contacted",
        "replied",
        "interested",
        "meeting_booked",
        "qualified",
        "disqualified",
        "closed",
      ];
      if (!validStatuses.includes(status)) {
        return { success: false, error: `Invalid lead status: ${status}` };
      }

      return await campaignServiceEnhanced.updateLead(leadId, {
        lead_status: status,
      });
    } catch (error) {
      console.error("Error in updateLeadStatus:", error);
      return { success: false, error: "Failed to update lead status" };
    }
  },

  /**
   * Record message sent to lead
   */
  recordMessageSent: async (leadId, campaignId, userId) => {
    try {
      const result = await campaignServiceEnhanced.updateLead(leadId, {
        message_sent: true,
        message_sent_at: new Date().toISOString(),
        lead_status: "contacted",
      });

      if (result.success) {
        await campaignServiceEnhanced.logActivity(
          campaignId,
          userId,
          "message_sent",
          "Message sent to lead",
          { lead_id: leadId }
        );
      }

      return result;
    } catch (error) {
      console.error("Error in recordMessageSent:", error);
      return { success: false, error: "Failed to record message" };
    }
  },

  /**
   * Record reply from lead
   */
  recordReply: async (
    leadId,
    campaignId,
    userId,
    replyContent,
    replyIntent = null
  ) => {
    try {
      const result = await campaignServiceEnhanced.updateLead(leadId, {
        replied: true,
        replied_at: new Date().toISOString(),
        reply_content: replyContent,
        reply_intent: replyIntent,
        lead_status: replyIntent === "interested" ? "interested" : "replied",
      });

      if (result.success) {
        await campaignServiceEnhanced.logActivity(
          campaignId,
          userId,
          "reply_received",
          "Reply received from lead",
          { lead_id: leadId, reply_intent: replyIntent }
        );
      }

      return result;
    } catch (error) {
      console.error("Error in recordReply:", error);
      return { success: false, error: "Failed to record reply" };
    }
  },

  /**
   * Log campaign activity
   */
  logActivity: async (
    campaignId,
    userId,
    activityType,
    description,
    metadata = {}
  ) => {
    try {
      const validActivityTypes = [
        "created",
        "updated",
        "started",
        "paused",
        "resumed",
        "completed",
        "leads_fetched",
        "message_sent",
        "reply_received",
      ];

      if (!validActivityTypes.includes(activityType)) {
        console.warn(`Invalid activity type: ${activityType}`);
        return { success: false, error: "Invalid activity type" };
      }

      const { data, error } = await supabase
        .from("campaign_activities")
        .insert({
          campaign_id: campaignId,
          user_id: userId,
          activity_type: activityType,
          description,
          metadata,
        })
        .select()
        .single();

      if (error) {
        console.warn("Failed to log activity:", error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error("Error in logActivity:", error);
      return { success: false, error: "Failed to log activity" };
    }
  },

  /**
   * Get campaign activity history
   */
  getCampaignActivityHistory: async (campaignId, limit = 50) => {
    try {
      const { data, error } = await supabase
        .from("campaign_activities")
        .select("*")
        .eq("campaign_id", campaignId)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error("Error in getCampaignActivityHistory:", error);
      return { success: false, error: "Failed to load activity history" };
    }
  },

  /**
   * Get campaign statistics
   */
  getCampaignStatistics: async (campaignId) => {
    try {
      const { data: stats, error } = await supabase
        .from("vw_campaign_engagement_metrics")
        .select("*")
        .eq("campaign_id", campaignId)
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: stats };
    } catch (error) {
      console.error("Error in getCampaignStatistics:", error);
      return { success: false, error: "Failed to load statistics" };
    }
  },

  /**
   * Get campaign summary view
   */
  getCampaignSummary: async (userId) => {
    try {
      const { data, error } = await supabase
        .from("vw_campaign_summary")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error("Error in getCampaignSummary:", error);
      return { success: false, error: "Failed to load campaign summary" };
    }
  },

  /**
   * Delete campaign (soft delete - should be handled at app level)
   */
  deleteCampaign: async (campaignId, userId) => {
    try {
      // Get campaign first to verify ownership
      const { data: campaign, error: fetchError } = await supabase
        .from("campaigns_enhanced")
        .select("id")
        .eq("id", campaignId)
        .eq("user_id", userId)
        .single();

      if (fetchError || !campaign) {
        return { success: false, error: "Campaign not found or access denied" };
      }

      // Delete campaign (cascades to leads and activities)
      const { error: deleteError } = await supabase
        .from("campaigns_enhanced")
        .delete()
        .eq("id", campaignId);

      if (deleteError) {
        return { success: false, error: deleteError.message };
      }

      return { success: true, message: "Campaign deleted successfully" };
    } catch (error) {
      console.error("Error in deleteCampaign:", error);
      return { success: false, error: "Failed to delete campaign" };
    }
  },
};

export default campaignServiceEnhanced;
